import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiX, FiCpu, FiUser, FiBriefcase, FiPhone, FiMail, FiSave, FiActivity } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { LeadExecutionEngine } from '../../../api/LeadExecutionEngine';

const SupremeLeadModal = ({ lead, onClose }) => {
    if (!lead) return null;

    // Estado do Formulário de Edição
    const [formData, setFormData] = useState({
        razaoSocial: lead.razaoSocial || lead.empresa?.razaoSocial || '',
        cnpj: lead.cnpj || lead.empresa?.cnpj || '',
        empresaPorte: lead.empresaPorte || lead.empresa?.porte || '',
        qtdeLinhas: lead.qtdeLinhas || lead.logistica?.qtdeLinhas || '',
        email: lead.email || '',
        phone: lead.phone || lead.telefone || ''
    });
    
    const [loading, setLoading] = useState(false);

    const handleSave = async () => {
        setLoading(true);
        try {
            const success = await LeadExecutionEngine.evoluirDossier(lead._id, formData);
            if (success) {
                setTimeout(() => window.location.reload(), 1000); // Reload para ver novos dados no painel
            }
        } catch (error) {
            toast.error("Falha ao salvar as alterações.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl"
        >
            <motion.div 
                initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
                className="w-full max-w-5xl max-h-[95vh] bg-[var(--bg-card)] border-2 border-[var(--border-color)] rounded-[3rem] overflow-hidden flex flex-col shadow-2xl"
            >
                <header className="p-8 border-b border-[var(--border-color)] flex justify-between items-center bg-[var(--text-primary)]/[0.03]">
                    <div className="flex items-center gap-6">
                        <div className="w-16 h-16 bg-[var(--accent-color)] rounded-2xl flex items-center justify-center text-black shadow-lg">
                            <FiCpu size={32} className="animate-pulse" />
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="px-3 py-1 bg-[var(--accent-color)] text-black text-[9px] font-black rounded uppercase tracking-widest">Dossiê B2B Prospect</span>
                                <span className="text-[var(--text-secondary)] text-[10px] font-bold opacity-40 uppercase">ID: {lead._id}</span>
                            </div>
                            <h2 className="text-4xl font-black italic tracking-tighter text-[var(--text-primary)] uppercase leading-none mt-1">Lead: {lead.name || lead.nome}</h2>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-4 hover:bg-red-500 hover:text-white text-[var(--text-primary)] rounded-2xl transition-all border border-[var(--border-color)] group">
                        <FiX size={24} className="group-hover:rotate-90 transition-transform" />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto p-10 space-y-10 custom-scrollbar bg-[var(--bg-main)]">
                    
                    {/* RADAR INTERNO */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-1 space-y-6 bg-[var(--bg-card)] p-8 rounded-[2.5rem] border border-[var(--border-color)] shadow-sm">
                            <div className="flex items-center gap-2 mb-4">
                                <FiActivity className="text-accent" />
                                <h3 className="text-xs font-black uppercase tracking-widest">STATUS ATUAL</h3>
                            </div>
                            <div className="p-5 bg-[var(--accent-color)]/10 border border-[var(--accent-color)]/20 rounded-2xl">
                                <span className="text-2xl font-black text-[var(--accent-color)] uppercase block">{lead.statusComercial}</span>
                                <span className="text-[9px] font-bold uppercase opacity-50 block mt-2">Criado em: {new Date(lead.createdAt).toLocaleDateString()}</span>
                            </div>
                        </div>

                        {/* FORMULÁRIO DE EDIÇÃO DO DOSSIÊ */}
                        <div className="lg:col-span-2 space-y-6 bg-[var(--bg-card)] p-8 rounded-[2.5rem] border border-[var(--border-color)] shadow-sm relative">
                            <div className="flex justify-between items-center mb-6">
                                <div className="flex items-center gap-2">
                                    <FiBriefcase className="text-accent" />
                                    <h3 className="text-xs font-black uppercase tracking-widest">DADOS CADASTRAIS DA EMPRESA</h3>
                                </div>
                                <button 
                                    onClick={handleSave} 
                                    disabled={loading}
                                    className="flex items-center gap-2 px-6 py-2 bg-[var(--accent-color)] text-black font-black uppercase text-[10px] tracking-widest rounded-xl hover:scale-105 transition-all active:scale-95 disabled:opacity-50"
                                >
                                    <FiSave size={14} />
                                    {loading ? 'Salvando...' : 'Atualizar Lead'}
                                </button>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <InputBox 
                                    label="Razão Social" 
                                    value={formData.razaoSocial} 
                                    onChange={(e) => setFormData({...formData, razaoSocial: e.target.value})} 
                                    icon={FiBriefcase} 
                                />
                                <InputBox 
                                    label="CNPJ" 
                                    value={formData.cnpj} 
                                    onChange={(e) => setFormData({...formData, cnpj: e.target.value})} 
                                    icon={FiBriefcase} 
                                />
                                <InputBox 
                                    label="Porte da Empresa" 
                                    value={formData.empresaPorte} 
                                    onChange={(e) => setFormData({...formData, empresaPorte: e.target.value})} 
                                    icon={FiUser} 
                                />
                                <InputBox 
                                    label="Quantidade de Linhas" 
                                    value={formData.qtdeLinhas} 
                                    type="number"
                                    onChange={(e) => setFormData({...formData, qtdeLinhas: e.target.value})} 
                                    icon={FiActivity} 
                                />
                                <InputBox 
                                    label="E-mail" 
                                    value={formData.email} 
                                    onChange={(e) => setFormData({...formData, email: e.target.value})} 
                                    icon={FiMail} 
                                />
                                <InputBox 
                                    label="Telefone / WhatsApp" 
                                    value={formData.phone} 
                                    onChange={(e) => setFormData({...formData, phone: e.target.value})} 
                                    icon={FiPhone} 
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <footer className="p-6 border-t border-[var(--border-color)] bg-[var(--bg-card)] flex justify-between items-center text-[9px] font-black uppercase opacity-20 tracking-[0.6em]">
                    <span>MOS DIPLOMACY • CRM_V2.0</span>
                    <span>B2B_LEAD_MODULE_ACTIVE</span>
                </footer>
            </motion.div>
        </motion.div>
    );
};

const InputBox = ({ label, value, onChange, icon: Icon, type = "text" }) => (
    <div className="space-y-2">
        <label className="text-[9px] font-black uppercase opacity-40 px-2 tracking-widest flex items-center gap-2">
            <Icon size={10} className="text-accent" /> {label}
        </label>
        <input 
            type={type}
            value={value} 
            onChange={onChange} 
            className="w-full h-12 px-4 rounded-xl bg-black/40 border border-white/10 text-white text-sm outline-none focus:border-accent transition-all font-bold" 
            placeholder={`Informar ${label}...`}
        />
    </div>
);

export default SupremeLeadModal;
